/**
 * Match `/portal/orders/{id}/line-comments` bundles to an order's line items.
 *
 * The bundles carry the TRUE Sage value of each line's `CommentText$` (read
 * from the `sage_sales_order_lines` mirror). The order's own
 * `OrderLineItem.sage_comment_text` is a different thing — it is what the
 * portal last SENT, and after an Edit-in-Sage append it holds only that
 * addition while Sage holds `existing | addition`. Anything that needs to show
 * or reason about what Sage actually says must use the bundle, never the
 * outbound field (kanban 099373a1).
 *
 * One rule, shared by OrderDetailModal (read-side chips) and SageEditModal
 * (the append editor) so the two surfaces can never disagree about which
 * comment belongs to which line.
 *
 * The endpoint returns ONLY bundles that carry content (see
 * `get_order_line_comments`), so the list is SPARSE and its array index means
 * nothing. Each bundle instead carries `position`: the 0-based ordinal of that
 * product line in Sage's natural order, assigned BEFORE the content filter.
 * That ordinal lines up with our own line index whenever the two sides list
 * products in the same order, which is the normal case.
 *
 *   1. A bundle labelled with a SKU is matched by that SKU only — the first
 *      unconsumed bundle with the same `item_code`, preferring one whose
 *      `position` equals this line's index (handles same-SKU duplicates that
 *      Sage holds in a different order). A labelled bundle is NEVER handed to a
 *      line with a different SKU, positionally or otherwise.
 *   2. Only a bundle with NO `item_code` (a product row Sage emitted without a
 *      code — rare) may be matched positionally, by `position === index`.
 *
 * Staging E2E 2026-09-09 on AU01844 is why rule 1 is strict: the list held a
 * single bundle (F1325.55, "MI only"); the old index-based fallback handed it
 * to line 0 (F3316.05), and F1325.55 then found its own bundle consumed. The
 * editor would have told the CSR the wrong line already said "MI only".
 */
import type { LineCommentBundle } from '../../services/portalOrders';

export interface MatchableLine {
  id: string;
  item_code: string;
}

const norm = (code: string | null | undefined): string => (code ?? '').trim();

export const matchLineCommentBundles = (
  lineItems: readonly MatchableLine[],
  bundles: readonly LineCommentBundle[] | null | undefined,
): Map<string, LineCommentBundle> => {
  const map = new Map<string, LineCommentBundle>();
  if (!bundles || bundles.length === 0) return map;
  const consumed = new Set<number>();

  lineItems.forEach((li, idx) => {
    const code = norm(li.item_code);
    let chosen = -1;

    if (code !== '') {
      // Rule 1: same SKU, preferring the bundle at this line's ordinal.
      const candidates: number[] = [];
      bundles.forEach((b, i) => {
        if (!consumed.has(i) && norm(b.item_code) === code) candidates.push(i);
      });
      if (candidates.length > 0) {
        const atOrdinal = candidates.find((i) => bundles[i].position === idx);
        chosen = atOrdinal ?? candidates[0];
      }
    }

    if (chosen === -1) {
      // Rule 2: an unlabelled bundle at this ordinal, and nothing else.
      chosen = bundles.findIndex(
        (b, i) => !consumed.has(i) && norm(b.item_code) === '' && b.position === idx,
      );
    }

    if (chosen !== -1) {
      consumed.add(chosen);
      map.set(li.id, bundles[chosen]);
    }
  });
  return map;
};
